import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Banner, Button, Card, DataTable, Field, Modal, Pill, Spinner, Td, Textarea } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { fmtDateTime, money } from '../lib/format';
import { useAsync } from '../lib/useAsync';

/** Mirrors the backend's ApprovalRow. */
export interface Approval {
  id: string;
  jobId: string;
  actionType: 'waiver_sign' | 'lien_file' | 'lien_release' | 'demand_letter' | 'job_close';
  label: string;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  requestedByUserId: string;
  requestedAt: string;
  expiresAt: string;
  reviewNote: string | null;
  amount: number | null;
  actionPayloadJson: Record<string, unknown>;
  job: { id: string; propertyAddress: string; county: string; status: string } | null;
}

const hoursLeft = (iso: string) => Math.round((new Date(iso).getTime() - Date.now()) / 3_600_000);

/**
 * The office queue. Every row here is a high-stakes action somebody in the
 * field asked for and could not perform themselves; approving replays their
 * exact payload, so the reviewer is signing off on what was actually requested.
 */
export default function Approvals() {
  const [reviewing, setReviewing] = useState<Approval | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const { data, loading, error: loadError, reload } = useAsync(
    () => api.get<{ approvals: Approval[] }>('/api/approvals?status=pending'),
    [],
  );

  const act = async (verb: 'approve' | 'reject') => {
    if (!reviewing) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/approvals/${reviewing.id}/${verb}`, { note: note || null });
      setDone(verb === 'approve' ? `Approved — ${reviewing.label.toLowerCase()} executed.` : 'Request rejected.');
      setReviewing(null);
      setNote('');
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : `Could not ${verb} the request`);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Spinner label="Loading approvals…" />;
  if (loadError) return <Banner tone="urgent" title="Could not load approvals">{loadError}</Banner>;

  const approvals = data?.approvals ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-ink">Pending approvals</h1>
        <p className="text-sm text-muted">
          {approvals.length} request{approvals.length === 1 ? '' : 's'} waiting. Requests expire 24 hours after they are filed.
        </p>
      </div>

      {done && <Banner tone="ok">{done}</Banner>}
      {error && <Banner tone="urgent">{error}</Banner>}

      <Card>
        <DataTable
          head={['Action', 'Job', 'Amount', 'Requested', 'Expires', '']}
          empty="Nothing waiting on you. Field requests land here."
        >
          {approvals.map((a) => {
            const hrs = hoursLeft(a.expiresAt);
            return (
              <tr key={a.id} className="border-b border-border/60">
                <Td className="font-medium">{a.label}</Td>
                <Td>
                  {a.job ? (
                    <Link to={`/jobs/${a.job.id}`} className="text-ink hover:text-tint">
                      {a.job.propertyAddress}
                    </Link>
                  ) : (
                    <span className="text-dim">Job removed</span>
                  )}
                </Td>
                <Td className="text-muted">{a.amount !== null ? money(a.amount) : '—'}</Td>
                <Td className="text-muted">{fmtDateTime(a.requestedAt)}</Td>
                <Td>
                  <Pill tone={hrs <= 4 ? 'urgent' : hrs <= 12 ? 'soon' : 'watch'}>
                    {hrs <= 0 ? 'Expiring' : `${hrs}h left`}
                  </Pill>
                </Td>
                <Td>
                  <Button
                    onClick={() => {
                      setReviewing(a);
                      setNote('');
                      setError(null);
                    }}
                    className="px-3 py-1 text-xs"
                  >
                    Review &amp; approve
                  </Button>
                </Td>
              </tr>
            );
          })}
        </DataTable>
      </Card>

      {reviewing && (
        <Modal title={reviewing.label} onClose={() => setReviewing(null)} wide>
          <div className="space-y-4">
            <div className="rounded-lg border border-border bg-surface-alt/40 p-4 text-sm">
              <p className="font-semibold text-ink">{reviewing.job?.propertyAddress ?? 'Job removed'}</p>
              <p className="mt-0.5 text-xs text-muted">
                Requested {fmtDateTime(reviewing.requestedAt)} · expires {fmtDateTime(reviewing.expiresAt)}
                {' · '}
                {Math.max(0, hoursLeft(reviewing.expiresAt))}h left
              </p>
              {reviewing.job && (
                <Link to={`/jobs/${reviewing.job.id}`} className="mt-2 inline-block text-xs font-semibold text-tint hover:underline">
                  Open the full job →
                </Link>
              )}
            </div>

            <div>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-dim">What will be executed</p>
              <pre className="max-h-64 overflow-auto rounded-lg border border-border bg-bg p-3 text-xs text-muted">
                {JSON.stringify(redact(reviewing.actionPayloadJson), null, 2)}
              </pre>
            </div>

            <Field label="Review note (optional)">
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Checked against invoice #2 and the signed change order." />
            </Field>

            {error && <Banner tone="urgent">{error}</Banner>}

            <div className="flex justify-end gap-2 border-t border-border pt-4">
              <Button variant="secondary" onClick={() => setReviewing(null)} disabled={busy}>
                Cancel
              </Button>
              <Button variant="danger" onClick={() => void act('reject')} disabled={busy}>
                Reject
              </Button>
              <Button onClick={() => void act('approve')} disabled={busy}>
                {busy ? 'Working…' : 'Approve and execute'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/** A base64 signature is thousands of characters of noise in a review panel. */
function redact(payload: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...payload };
  if (typeof out.signatureImage === 'string') {
    out.signatureImage = out.signatureImage.startsWith('data:') ? '<signature captured>' : out.signatureImage;
  }
  return out;
}
